import { FlightError } from "@b2bportal/air-booking-api";
import { CardErrorEnum, type CardError } from "@b2bportal/card-api";
import { type LodgingError, LodgingErrorEnum } from "@b2bportal/lodging-api";
import {
  Payment,
  type PaymentError,
  type ProductError,
  type PurchaseError,
} from "@b2bportal/purchase-api";
import { PurchaseErrorEnum, Product } from "@b2bportal/purchase-api";

const cardErrorOptions = Object.values(
  CardErrorEnum
) as (keyof typeof CardErrorEnum)[];

const flightErrorOptions = Object.values(
  FlightError
) as (keyof typeof FlightError)[];

const lodgingErrorOptions = Object.values(
  LodgingErrorEnum
) as (keyof typeof LodgingErrorEnum)[];

const purchaseErrorOptions = Object.values(
  PurchaseErrorEnum
) as (keyof typeof PurchaseErrorEnum)[];

const productTypeOptions = ["Hotel", "Flight"] as (keyof typeof Product)[];
const paymentTypeOptions = ["Card"] as (keyof typeof Payment)[];

export function PurchaseErrorForm<T extends { errors: Array<PurchaseError> }>({
  errorObj,
  onChange,
}: {
  errorObj: T;
  onChange: (newObj: T) => void;
}) {
  return (
    <>
      <p
        style={{
          fontWeight: "normal",
          fontSize: 18,
        }}
      >
        {"Error"}
      </p>
      <div style={{ margin: "8px 0", paddingLeft: 12 }}>
        {/* select the PurchaseError "Error" field */}
        <select
          value={errorObj.errors[0].Error}
          onChange={(e) => {
            const errorKey = e.target.value as PurchaseErrorEnum;
            let newError: PurchaseError;
            if (errorKey === PurchaseErrorEnum.ProductError) {
              newError = {
                Error: PurchaseErrorEnum.ProductError,
                value: {
                  type: Product.Flight,
                  value: flightErrorOptions[0],
                },
              };
            } else if (errorKey === PurchaseErrorEnum.PaymentError) {
              newError = {
                Error: PurchaseErrorEnum.PaymentError,
                tags: {},
                value: {
                  type: Payment.Card,
                  value: {
                    CardError: CardErrorEnum.ExpiredCard,
                  } satisfies CardError,
                },
              };
            } else {
              newError = { Error: errorKey } as PurchaseError;
            }
            onChange({ ...errorObj, errors: [newError] });
          }}
        >
          {purchaseErrorOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>

        {/* if it’s a ProductError, show nested selects */}
        {errorObj.errors[0].Error === PurchaseErrorEnum.ProductError && (
          <>
            <select
              style={{ marginLeft: 8 }}
              value={errorObj.errors[0].value.type}
              onChange={(e) => {
                if (e.target.value === Product.Hotel) {
                  const newError = {
                    Error: PurchaseErrorEnum.ProductError,
                    value: {
                      value: {
                        LodgingError: LodgingErrorEnum.CheckInMinimumAgeNotMet,
                      } satisfies LodgingError,
                      type: e.target.value,
                    },
                  } satisfies ProductError;
                  onChange({ ...errorObj, errors: [newError] });
                } else if (e.target.value === Product.Flight) {
                  const newError = {
                    Error: PurchaseErrorEnum.ProductError,
                    value: {
                      value: FlightError.CheckInMinimumAgeNotMet,
                      type: e.target.value,
                    },
                  } satisfies ProductError;
                  onChange({ ...errorObj, errors: [newError] });
                }
              }}
            >
              {productTypeOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <select
              style={{ marginLeft: 8 }}
              value={
                errorObj.errors[0].value.type === "Hotel"
                  ? errorObj.errors[0].value.value.LodgingError
                  : errorObj.errors[0].value.value
              }
              onChange={(e) => {
                if (errorObj.errors[0].Error !== "ProductError") return;
                if (errorObj.errors[0].value.type === Product.Hotel) {
                  const newError = {
                    Error: PurchaseErrorEnum.ProductError,
                    value: {
                      value: {
                        LodgingError: e.target.value,
                      },
                      type: Product.Hotel,
                    },
                  } satisfies ProductError;
                  onChange({ ...errorObj, errors: [newError] });
                } else if (errorObj.errors[0].value.type === Product.Flight) {
                  const newError = {
                    Error: PurchaseErrorEnum.ProductError,
                    value: {
                      value: e.target.value,
                      type: Product.Flight,
                    },
                  } satisfies ProductError;
                  onChange({ ...errorObj, errors: [newError] });
                }
              }}
            >
              {errorObj.errors[0].value.type === "Flight" &&
                flightErrorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              {errorObj.errors[0].value.type === "Hotel" &&
                lodgingErrorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
            </select>
          </>
        )}
        {errorObj.errors[0].Error === PurchaseErrorEnum.PaymentError && (
          <>
            <select
              style={{ marginLeft: 8 }}
              value={errorObj.errors[0].value.type}
              onChange={(e) => {
                if (e.target.value === Payment.Card) {
                  const newError: PaymentError = {
                    Error: PurchaseErrorEnum.PaymentError,
                    tags: {},
                    value: {
                      value: {
                        CardError: CardErrorEnum.ExpiredCard,
                      } satisfies CardError,
                      type: e.target.value,
                    },
                  };
                  onChange({ ...errorObj, errors: [newError] });
                } else {
                  const newError: PaymentError = {
                    Error: PurchaseErrorEnum.PaymentError,
                    tags: {},
                    value: {
                      value: "mockValue",
                      type: e.target.value as Payment,
                    },
                  };
                  onChange({ ...errorObj, errors: [newError] });
                }
              }}
            >
              {paymentTypeOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {errorObj.errors[0].value.type === Payment.Card && (
              <select
                style={{ marginLeft: 8 }}
                value={errorObj.errors[0].value.type}
                onChange={(e) => {
                  if (errorObj.errors[0].Error !== "PaymentError") return;
                  const newError: PaymentError = {
                    Error: PurchaseErrorEnum.PaymentError,
                    tags: {},
                    value: {
                      value: {
                        CardError: e.target.value,
                      },
                      type: Payment.Card,
                    },
                  };
                  onChange({ ...errorObj, errors: [newError] });
                }}
              >
                {cardErrorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
      </div>
    </>
  );
}
